import type { OnboardingPayload } from "@/types/onboarding";
import { splitLegacyUserName } from "@/lib/personName";
import type { UserData } from "@/types/user-data";

/** Remove keys whose value is `undefined` so PostgREST does not send them. */
export function stripUndefined<T extends Record<string, unknown>>(obj: T): Partial<T> {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(obj)) {
    if (v !== undefined) out[k] = v;
  }
  return out as Partial<T>;
}

/** Drop undefined nested keys for JSONB columns. */
export function sanitizeOnboardingPayload(o: OnboardingPayload): OnboardingPayload {
  return JSON.parse(JSON.stringify(o)) as OnboardingPayload;
}

/** Core row for `public.users` INSERT — avoids failing when optional columns (e.g. `onboarding` jsonb) are missing on older DBs. */
export function buildUsersInsertRow(
  profileData: Omit<UserData, "created_at">
): Pick<UserData, "user_id" | "email" | "user_name"> &
  Partial<
    Pick<
      UserData,
      | "workspace_persona"
      | "district_name"
      | "custom_district_name"
      | "school_name"
      | "custom_school_name"
      | "first_name"
      | "last_name"
    >
  > {
  // AE-207: onboarding still collects one name field, so derive the two columns here with
  // the same first-space split as the migration backfill. Otherwise a brand new user would
  // land with NULL first_name/last_name right after the backfill fixed every existing row.
  const legacy = splitLegacyUserName(profileData.user_name);

  return stripUndefined({
    user_id: profileData.user_id,
    email: profileData.email,
    user_name: profileData.user_name,
    // `||`, not `??`: splitLegacyUserName always returns strings, so `??` would let an empty
    // string through and insert `first_name: ""`. That also defeats the migration's
    // `WHERE first_name IS NULL` re-run guard for the row.
    first_name: profileData.first_name ?? (legacy.firstName || undefined),
    last_name: profileData.last_name ?? (legacy.lastName || undefined),
    workspace_persona: profileData.workspace_persona,
    district_name: profileData.district_name,
    custom_district_name: profileData.custom_district_name,
    school_name: profileData.school_name,
    custom_school_name: profileData.custom_school_name,
  }) as Pick<UserData, "user_id" | "email" | "user_name"> &
    Partial<
      Pick<
        UserData,
        | "workspace_persona"
        | "district_name"
        | "custom_district_name"
        | "school_name"
        | "custom_school_name"
        | "first_name"
        | "last_name"
      >
    >;
}

export function formatSupabaseProfileError(error: unknown): string {
  if (error && typeof error === "object" && "message" in error) {
    const m = (error as { message?: string }).message;
    if (typeof m === "string" && m.trim()) return m.trim();
  }
  if (error instanceof Error && error.message) return error.message;
  return "Something went wrong saving your profile. Please try again.";
}

export function isUniqueViolation(error: unknown): boolean {
  return (
    typeof error === "object" &&
    error !== null &&
    "code" in error &&
    (error as { code?: string }).code === "23505"
  );
}

/**
 * PostgREST when a column is absent from the OpenAPI schema (or not in DB), e.g.:
 * "Could not find the 'workspace_persona' column of 'users' in the schema cache"
 */
export function missingColumnFromPostgrestSchemaCache(error: unknown): string | null {
  const msg = formatSupabaseProfileError(error);
  const m = msg.match(/Could not find the '([^']+)' column/);
  return m?.[1] ?? null;
}
